import React, { useMemo, useState } from "react";
import { useForm } from "react-final-form";
import { fetchQuery, graphql } from "react-relay";

import { WordListConfigContainer_settings as SettingsData } from "coral-admin/__generated__/WordListConfigContainer_settings.graphql";
import SiteSearchDropdown from "coral-admin/components/SiteSearchDropdown/SiteSearchDropdown";
import { useCoralContext } from "coral-framework/lib/bootstrap";
import {
  purgeMetadata,
  withFragmentContainer,
} from "coral-framework/lib/relay";
import { HorizontalGutter } from "coral-ui/components/v2";

import BannedWordListConfig from "./BannedWordListConfig";
import SuspectWordListConfig from "./SuspectWordListConfig";

interface Props {
  submitting: boolean;
  settings: SettingsData;
}

const SITE_WORDLIST_QUERY = graphql`
  query WordListConfigContainer_SiteQuery($siteID: ID!) {
    site(id: $siteID) {
      id
      name
      wordList {
        banned
        suspect
      }
    }
  }
`;

const WordListConfigContainer: React.FunctionComponent<Props> = ({
  settings,
  submitting,
}) => {
  const form = useForm();
  const [selectedSiteID, setSelectedSiteID] = useState<string>("");

  const { relayEnvironment } = useCoralContext();

  useMemo(() => form.initialize(purgeMetadata(settings)), []);

  const handleSiteChange = async (siteID: string) => {
    setSelectedSiteID(siteID);
    if (siteID) {
      try {
        const siteData = await fetchQuery<any>(
          relayEnvironment,
          SITE_WORDLIST_QUERY,
          {
            siteID,
          }
        );
        const wordList = siteData?.site?.wordList;
        form.initialize({
          ...purgeMetadata(settings),
          wordList: wordList ?? { banned: [], suspect: [] },
          siteID,
        });
      } catch (error) {
        form.initialize(purgeMetadata(settings));
      }
    } else {
      form.initialize(purgeMetadata(settings));
    }
  };

  return (
    <HorizontalGutter size="double" data-testid="configure-wordListContainer">
      <SiteSearchDropdown
        value={selectedSiteID}
        onChange={handleSiteChange}
        placeholder="Select site"
        disabled={submitting}
      />
      <BannedWordListConfig disabled={submitting} />
      <SuspectWordListConfig
        disabled={submitting}
        premoderateSuspectWords={settings.premoderateSuspectWords}
      />
    </HorizontalGutter>
  );
};

const enhanced = withFragmentContainer<Props>({
  settings: graphql`
    fragment WordListConfigContainer_settings on Settings {
      ...SuspectWordListConfig_formValues @relay(mask: false)
      ...BannedWordListConfig_formValues @relay(mask: false)
    }
  `,
})(WordListConfigContainer);

export default enhanced;
